import { startUserPresence, stopUserPresence } from './user_presence_runtime.js';
import {
  clearCurrentUser,
  getCurrentUser,
  isAdminUser,
  setCurrentUser
} from './current_user.js?v=20260722a';
import { openUserAdminModal } from '../app/lazy_dialog_runtime.js?v=20260904f';
import { loadI18nHealthStatus } from '../i18n/i18n_service.js?v=20260722a';
import { leaveCurrentOrder } from '../project/order_editing_session.js?v=20260731a';
import { disconnectMessageRuntime } from '../messages/message_runtime.js?v=20260806a';

const runtimeCallbacks = {
  refresh: null,
  refreshChromeOnly: null
};
let initialized = false;
let loginModal = null;
let profileModal = null;

export function configureRuntime(callbacks = {}) {
  if (typeof callbacks.refresh === 'function') runtimeCallbacks.refresh = callbacks.refresh;
  if (typeof callbacks.refreshChromeOnly === 'function') runtimeCallbacks.refreshChromeOnly = callbacks.refreshChromeOnly;
}

function requestRefresh(reason = 'auth') {
  if (typeof runtimeCallbacks.refreshChromeOnly === 'function') {
    runtimeCallbacks.refreshChromeOnly(reason);
  } else if (typeof runtimeCallbacks.refresh === 'function') {
    runtimeCallbacks.refresh(false);
  }
}

async function fetchSessionUser() {
  const response = await fetch('/api/auth/me', { method: 'GET' });
  const payload = await response.json().catch(() => ({}));
  return response.ok && payload.user ? payload.user : null;
}

// /api/auth/me never 401s -- an unauthenticated caller gets a 200 with
// user:null (see handleMe() in backend/routes/auth.js), so apiJson()'s
// retry-on-401 doesn't cover this call at all. That matters more here than
// anywhere else: initAuth() below treats a failed restoreSession() as
// grounds for a full window.location.reload() when there's no previous
// user to fall back to -- a single transient "no user" response here was
// silently forcing users back to the login screen (Nutzer-Meldung
// 2026-08-27: the reload itself is visible in the nginx access log as a
// bare GET / between two login attempts, seconds after the first one
// already succeeded). One short-delayed retry before giving up.
export async function restoreSession() {
  try {
    let user = await fetchSessionUser();
    if (!user) {
      await new Promise(resolve => { setTimeout(resolve, 250); });
      user = await fetchSessionUser();
    }
    if (user) setCurrentUser(user);
    else clearCurrentUser();
  } catch (_error) {
    clearCurrentUser();
  }
}

function closeLoginModal() {
  loginModal?.remove();
  loginModal = null;
}

function closeProfileModal() {
  profileModal?.remove();
  profileModal = null;
}

export function openLoginDialog() {
  closeLoginModal();
  closeProfileModal();
  loginModal = document.createElement('div');
  loginModal.className = 'modal-backdrop auth-modal-root';
  loginModal.innerHTML = `
      <form class="modal auth-modal" id="authLoginForm">
        <div class="modal-title">Einloggen</div>
        <div class="auth-form">
          <label>Login-Name oder E-Mail
            <input id="authLoginName" name="login" type="text" autocomplete="username">
          </label>
          <label>Passwort
            <input id="authPassword" name="password" type="password" autocomplete="current-password">
          </label>
          <label class="auth-inline-field">
            <input id="authRemember" type="checkbox">
            Angemeldet bleiben
          </label>
        </div>
        <div id="authLoginMessage" class="small-text auth-message" aria-live="polite"></div>
        <div class="auth-modal-actions">
          <button class="btn" type="button" id="authCancelLogin">Abbrechen</button>
          <button class="btn primary" type="submit" id="authSubmitLogin">Einloggen</button>
        </div>
      </form>
    `;
  document.body.appendChild(loginModal);
  const form = loginModal.querySelector('#authLoginForm');
  const loginInput = loginModal.querySelector('#authLoginName');
  const passwordInput = loginModal.querySelector('#authPassword');
  const rememberInput = loginModal.querySelector('#authRemember');
  const message = loginModal.querySelector('#authLoginMessage');
  const submitButton = loginModal.querySelector('#authSubmitLogin');
  loginModal.querySelector('#authCancelLogin')?.addEventListener('click', closeLoginModal);
  form?.addEventListener('submit', event => {
    event.preventDefault();
    login(loginInput?.value || '', passwordInput?.value || '', {
      message,
      submitButton,
      remember: rememberInput?.checked === true
    });
  });
  loginInput?.focus();
}

async function login(loginName, password, controls = {}) {
  const { message, submitButton } = controls;
  if (submitButton) submitButton.disabled = true;
  if (message) {
    message.textContent = 'Login wird geprüft...';
    message.classList.remove('is-error');
  }
  try {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ login: loginName, password, remember: controls.remember === true })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload.ok === false || !payload.user) {
      throw new Error(payload.error || 'Login fehlgeschlagen.');
    }
    setCurrentUser(payload.user);
    startUserPresence();
    closeLoginModal();
    requestRefresh('auth-login');
  } catch (error) {
    if (message) {
      message.textContent = error?.message || 'Login fehlgeschlagen.';
      message.classList.add('is-error');
    }
  } finally {
    if (submitButton) submitButton.disabled = false;
  }
}

export async function logout() {
  stopUserPresence();
  disconnectMessageRuntime();
  try {
    await leaveCurrentOrder({ keepalive: true });
    await fetch('/api/auth/logout', { method: 'POST' });
  } catch (_error) {
    // Lokaler State wird auch dann geloescht, wenn der Logout-Call scheitert.
  }
  clearCurrentUser();
  closeProfileModal();
  window.location.reload();
}

export async function openProfileDialog(options = {}) {
  const user = getCurrentUser();
  if (!user) {
    openLoginDialog();
    return;
  }
  closeLoginModal();
  closeProfileModal();
  try {
    await openUserAdminModal({ onLogout: logout, initialTab: options.initialTab });
  } catch (error) {
    console.error('Benutzerprofil konnte nicht geladen werden', error);
    const status = document.getElementById('statusText') || document.getElementById('statusbar');
    if (status) status.textContent = `Benutzerprofil konnte nicht geladen werden: ${error?.message || error}`;
  }
}

export function initAuth() {
  if (initialized) return;
  initialized = true;
  startUserPresence();
  // Wird erst NACH einem bereits erfolgreichen Login-Gate-Durchlauf
  // aufgerufen (main.js's startStudio() ruft setCurrentUser() VOR
  // initApp()/initAuth()) -- der /api/auth/me-Aufruf hier ist also nur
  // noch eine Zweitpruefung. Ein einzelner transienter Fehlschlag dieser
  // Zweitpruefung (Netzwerk-Hickser, kurzzeitiges 401 hinter dem Reverse-
  // Proxy) darf eine bereits gueltige Session NICHT loeschen und die Seite
  // per reload() in eine "Website neu laden?"-Bestaetigungsschleife
  // schicken -- die alte Logik tat genau das (Nutzer-Meldung 2026-08-25,
  // studio.chappsolut.ch: Dialog direkt nach dem Login, "Neu laden" loest
  // ihn sofort wieder aus, nur "Abbrechen" liess die bereits lauffaehige
  // App unangetastet).
  const previousUser = getCurrentUser();
  restoreSession().then(() => {
    if (!getCurrentUser()) {
      if (previousUser) {
        setCurrentUser(previousUser);
        console.warn('Sitzungs-Zweitpruefung fehlgeschlagen, bestehende Login-Session bleibt aktiv.');
        return;
      }
      window.location.reload();
      return;
    }
    requestRefresh('auth-session');
    if (isAdminUser()) loadI18nHealthStatus();
  });
}
